/**
 * WAIT PLUGIN - Loading State Management
 *
 * A Nuxt plugin that provides a centralized way to manage loading states across your application.
 * This plugin creates a Vuex module to track multiple loading states and provides helper methods
 * to start, stop, and check loading states.
 *
 * USAGE GUIDELINES:
 *
 * 1. BASIC USAGE:
 *    this.$wait.start('my-loader')     // Start a loading state
 *    this.$wait.end('my-loader')       // End a loading state
 *    this.$wait.is('my-loader')        // Check if loading
 *
 * 2. WILDCARD SUPPORT:
 *    The plugin supports wildcard matching using '*' character:
 *    - this.$wait.is('api/*')          // Matches any loader starting with 'api/'
 *    - this.$wait.is('*users*')        // Matches any loader containing 'users'
 *    - this.$wait.is('form-*-submit')  // Matches 'form-login-submit', 'form-register-submit', etc.
 */

import { Plugin } from '@nuxt/types';
import Vue from 'vue';
import { Store, Module } from 'vuex';
import Wait from '../components/layout/Wait.vue';
export interface WaitHelpers {
  start: (loader: string) => void;
  end: (loader: string) => void;
  is: (loader: string) => boolean;
}

interface WaitState {
  loadingStates: Record<string, boolean>;
}

const MODULE_NAME = 'wait';

export const waitModule: Module<WaitState, any> = {
  namespaced: true,
  state: (): WaitState => ({
    loadingStates: {},
  }),
  mutations: {
    START(state: WaitState, loader: string) {
      Vue.set(state.loadingStates, loader, true);
    },
    END(state: WaitState, loader: string) {
      Vue.delete(state.loadingStates, loader);
    },
  },
  getters: {
    is:
      (state: WaitState) =>
      (loader: string): boolean => {
        // exact match (fast path)
        if (!loader.includes('*')) {
          return !!state.loadingStates[loader];
        }

        // simple wildcard support (one '*')
        const regex = new RegExp(
          '^' +
            loader
              .split('*')
              .map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
              .join('.*') +
            '$',
        );

        return Object.keys(state.loadingStates).some(id => regex.test(id));
      },
  },
};

export const waitVm = (store: Store<any>): WaitHelpers => {
  store.registerModule(MODULE_NAME, waitModule);

  const wait: WaitHelpers = {
    start: (loader: string) => store.commit(`${MODULE_NAME}/START`, loader),
    end: (loader: string) => store.commit(`${MODULE_NAME}/END`, loader),
    is: (loader: string) => store.getters[`${MODULE_NAME}/is`](loader),
  };

  return wait;
};

const waitPlugin: Plugin = ({ store }, inject) => {
  const module = waitVm(store);
  Vue.component('Wait', Wait);

  inject('wait', module);
};

export default waitPlugin;
